import { useEffect, useState } from "react";
import { Card, CardContent } from "./card";
import { CheckCircle2, Loader2, ArrowRightLeft, Wallet, DollarSign, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

interface ConversionProgressProps {
  open: boolean;
  fromCurrency: "BRL" | "USDC";
  toCurrency: "BRL" | "USDC";
  fromAmount: number;
  estimatedToAmount: number;
}

interface ProgressStep {
  id: string;
  message: string;
  icon: React.ReactNode;
  duration: number; // ms
}

const BRLD_TO_USDC_STEPS: ProgressStep[] = [
  {
    id: "connect",
    message: "Conectando ao pool de liquidez USDC...",
    icon: <Wallet className="w-5 h-5" />,
    duration: 2000,
  },
  {
    id: "calculate",
    message: "Calculando melhor taxa de conversão...",
    icon: <DollarSign className="w-5 h-5" />,
    duration: 2500,
  },
  {
    id: "execute",
    message: "Executando conversão de BRLD para USDC...",
    icon: <ArrowRightLeft className="w-5 h-5" />,
    duration: 3000,
  },
  {
    id: "blockchain",
    message: "Registrando operação na blockchain...",
    icon: <Sparkles className="w-5 h-5" />,
    duration: 2500,
  },
];

const USDC_TO_BRLD_STEPS: ProgressStep[] = [
  {
    id: "connect",
    message: "Conectando ao pool de liquidez...",
    icon: <Wallet className="w-5 h-5" />,
    duration: 2000,
  },
  {
    id: "calculate",
    message: "Calculando melhor taxa de conversão...",
    icon: <DollarSign className="w-5 h-5" />,
    duration: 2500,
  },
  {
    id: "execute",
    message: "Executando conversão de USDC para BRLD...",
    icon: <ArrowRightLeft className="w-5 h-5" />,
    duration: 3000,
  },
  {
    id: "blockchain",
    message: "Emitindo BRLD na blockchain...",
    icon: <Sparkles className="w-5 h-5" />,
    duration: 2500,
  },
];

export function ConversionProgress({
  open,
  fromCurrency,
  toCurrency,
  fromAmount,
  estimatedToAmount,
}: ConversionProgressProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  const steps = fromCurrency === "BRL" ? BRLD_TO_USDC_STEPS : USDC_TO_BRLD_STEPS;

  useEffect(() => {
    if (!open) {
      // Reset state when closed
      setCurrentStep(0);
      setCompletedSteps([]);
      return;
    }

    // Start the animation sequence
    let stepIndex = 0;
    setCurrentStep(0);
    setCompletedSteps([]);

    const runStep = () => {
      if (stepIndex >= steps.length) return;

      const step = steps[stepIndex];

      // After duration, mark as completed and move to next
      const timeout = setTimeout(() => {
        setCompletedSteps((prev) => [...prev, step.id]);
        stepIndex++;
        setCurrentStep(stepIndex);

        if (stepIndex < steps.length) {
          runStep();
        }
      }, step.duration);

      return timeout;
    };

    const timeout = runStep();

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [open, steps]);

  if (!open) return null;

  const formatAmount = (amount: number, currency: "BRL" | "USDC") => {
    if (currency === "BRL") {
      return (amount / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    }
    return (amount / 100).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4 overflow-hidden">
        <CardContent className="pt-8 pb-6 space-y-6">
          {/* Header com animação */}
          <div className="text-center space-y-2">
            <div className="relative mx-auto w-16 h-16">
              {/* Círculo animado de fundo */}
              <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
              {/* Ícone central */}
              <div className="relative w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <ArrowRightLeft className="w-8 h-8 text-primary animate-pulse" />
              </div>
            </div>
            <h2 className="text-xl font-semibold mt-4">Processando Conversão</h2>
            <p className="text-sm text-muted-foreground">
              {formatAmount(fromAmount, fromCurrency)} → {formatAmount(estimatedToAmount, toCurrency)}
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-3">
            {steps.map((step, index) => {
              const isCompleted = completedSteps.includes(step.id);
              const isCurrent = index === currentStep && !isCompleted;
              const isPending = index > currentStep;

              return (
                <div
                  key={step.id}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg transition-all duration-300",
                    isCompleted && "bg-green-50 border border-green-200",
                    isCurrent && "bg-primary/5 border border-primary/20",
                    isPending && "opacity-40"
                  )}
                >
                  {/* Ícone do step */}
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300",
                      isCompleted && "bg-green-500 text-white",
                      isCurrent && "bg-primary text-primary-foreground",
                      isPending && "bg-muted text-muted-foreground"
                    )}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : isCurrent ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      step.icon
                    )}
                  </div>

                  {/* Mensagem */}
                  <span
                    className={cn(
                      "text-sm transition-all duration-300",
                      isCompleted && "text-green-700 font-medium",
                      isCurrent && "text-foreground font-medium",
                      isPending && "text-muted-foreground"
                    )}
                  >
                    {step.message}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Mensagem de aguarde */}
          <p className="text-center text-xs text-muted-foreground">
            Por favor, aguarde. Não feche esta janela.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
